import { useCallback } from 'react';
import { useSetRecoilState } from 'recoil';

import api from '@/utils/api';
import { ApiUrl } from '@/utils/constants';

import alertState from '@/states/alertState';

import { PutVoipOptionChangeRequest } from '@/types/api/mypage/voipoptioninfo';

import { getSigV4HeadersForPut, isExpiredToken } from '@/utils/cognito';
import url from '@/utils/config';
import useSetLoading from '../useSetLoading';

/** VoIPオプション変更APIのURL */
const apiUrl = ApiUrl.Write.VOIP_OPTION_INFO;

/**
 * VoIPオプション変更APIの呼び出しフック。
 * 変更内容を保存しつつ、業務エラーは上位画面側で個別表示できるよう投げ返す。
 *
 * @returns execute - API実行関数
 */
const useCallAPIPutVoipOptionChange = () => {
  const { startLoading, finishLoading } = useSetLoading();
  const setAlert = useSetRecoilState(alertState);

  const execute = useCallback(async (params: PutVoipOptionChangeRequest) => {
    try {
      startLoading(ApiUrl.Write.VOIP_OPTION_INFO);
      const headers = await getSigV4HeadersForPut(apiUrl, params);
      await api.put(apiUrl, params, headers);
    } catch (error: any) {
      if (error.response) {
        if (isExpiredToken(error)) {
          // 認証切れ時は更新処理を続けずログインへ戻す。
          sessionStorage.clear();
          window.location.href = url('/login');
          return;
        }
        const { status } = error.response;
        if (status !== 200) {
          if (error.response.data) {
            const { data } = error.response;
            if ('error_code' in data) {
              // 業務エラー内容は呼び出し元で分岐表示するため、そのまま再送出する。
              throw error; // 上階層にエラーを通知する
            }
          }
        }
      }

      // 想定外エラーは共通アラートを出したうえで TypeError として通知する。
      setAlert({
        isOpen: true,
        apiType: '',
      });
      throw new TypeError(); // 上階層にTypeErrorで通知する
    } finally {
      finishLoading(ApiUrl.Write.VOIP_OPTION_INFO);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { execute };
};

export default useCallAPIPutVoipOptionChange;
